import { track } from '@vercel/analytics';
import { appVersion } from './appVersion';
import { trackClarityEvent } from './clarity';

/**
 * The custom analytics events the app reports to Vercel Analytics and
 * Clarity (see SENDS_TO_VERCEL for which go where). Keeping the
 * union exhaustive means renaming or removing an event surfaces every call
 * site at compile time, and typos can't create silent orphan event streams.
 */
type Validator = (value: unknown) => boolean;
const oneOf =
  (...values: (string | number | boolean)[]): Validator =>
  (value) =>
    values.includes(value as never);
const positive: Validator = (value) =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;
const offlineCount: Validator = (value) =>
  typeof value === 'number' &&
  Number.isInteger(value) &&
  value >= 0 &&
  value <= 999;
const boolean: Validator = (value) => typeof value === 'boolean';
const scope = { round_scope: oneOf('current', 'past') };
const teamScope = { team: oneOf(1, 2), ...scope };
const teamMadeFlow = {
  ...teamScope,
  flow: oneOf('total', 'nil_check', 'per_player'),
};
const limitContext = { context: oneOf('setup', 'reset', 'board', 'game_over') };

/** This registry is also the runtime privacy boundary for both vendors. */
export const EVENT_SCHEMA = {
  real_game_qualified: {},
  game_started: { ui_mode: oneOf('table', 'classic') },
  game_continued: { ui_mode: oneOf('table', 'classic') },
  game_won: { result: oneOf('win', 'tie') },
  viewer_session_joined: { source: oneOf('url', 'restored') },
  pwa_installed: {},
  offline_play_synced: { games: offlineCount, rounds: offlineCount },
  screen_session_started: {
    display_mode: oneOf('standalone', 'browser'),
    width: positive,
    visible_height: positive,
    dpr: positive,
    orientation: oneOf('portrait', 'landscape'),
    size_bucket: oneOf('compact', 'mobile', 'tablet', 'desktop'),
    small_screen: boolean,
  },
  share_tip_shown: { showing: oneOf(1, 2) },
  share_tip_opened: { showing: oneOf(1, 2) },
  share_tip_dismissed: {
    method: oneOf('close', 'outside'),
    showing: oneOf(1, 2),
  },
  share_modal_opened: {},
  share_link_copy_requested: {},
  link_copied: {},
  nil_scoring_rule_changed: {
    value: oneOf('takes_bags', 'helps_team_bid', 'no_bags_no_help'),
    context: oneOf('setup'),
  },
  team_made_opened: teamMadeFlow,
  team_made_confirmed: teamMadeFlow,
  team_made_nil_answered: teamScope,
  made_error_reopened: { round_scope: oneOf('current', 'past') },
  ui_mode_changed: { value: oneOf('table', 'classic') },
  summary_view_changed: { value: oneOf('compact', 'extended', 'table') },
  past_round_expanded: {},
  past_round_collapsed: {},
  round_details_opened: {},
  round_details_closed: {},
  dealer_picker_opened: {},
  dealer_selected: {},
  dealer_changed: {},
  undo_used: {
    context: oneOf('scoring', 'viewer_leave'),
    input_kind: oneOf('bid', 'made', 'team_made'),
    ...scope,
  },
  score_limit_edit_opened: {},
  score_limit_edited: {
    context: oneOf('board', 'game_over'),
    limited: boolean,
  },
  settings_opened: {},
  game_reset: {
    source: oneOf('header', 'home', 'game_over'),
    teams: oneOf('same', 'different'),
  },
  home_opened: {},
  menu_opened: {},
  menu_closed: {},
  new_game_requested: { source: oneOf('header', 'home', 'game_over') },
  game_start_requested: {},
  game_continue_requested: { source: oneOf('home', 'game_over') },
  overtime_continued: { source: oneOf('game_over') },
  reset_cancelled: {},
  reset_confirmed: {},
  reset_teams_selected: { value: oneOf('same', 'different') },
  player_name_edit_opened: {},
  team_name_edit_opened: {},
  player_bid_opened: scope,
  player_made_opened: scope,
  scoring_selection_confirmed: { input_kind: oneOf('bid', 'made'), ...scope },
  score_help_opened: {},
  score_help_closed: {},
  game_score_help_opened: {},
  feedback_support_opened: {},
  about_opened: {},
  privacy_notice_opened: {},
  score_limit_entry_opened: limitContext,
  score_limit_declined: limitContext,
  score_limit_confirmed: limitContext,
  score_limit_cancelled: limitContext,
  keypad_digit_pressed: { context: oneOf('score_limit') },
  keypad_backspace_pressed: { context: oneOf('score_limit') },
  keypad_no_limit_selected: { context: oneOf('score_limit') },
  watching_menu_opened: {},
  watching_menu_closed: {},
  viewer_seat_picker_opened: { source: oneOf('automatic', 'menu') },
  viewer_seat_selected: {},
  viewer_session_left: {},
  install_requested: { source: oneOf('header', 'prompt', 'download') },
  install_prompt_dismissed: { method: oneOf('close', 'later') },
  update_requested: {},
  update_dismissed: { method: oneOf('close', 'later') },
  modal_dismissed: {
    modal: oneOf(
      'settings',
      'share',
      'viewer_seat',
      'dealer',
      'scoring',
      'game_over',
      'reset',
      'score_limit',
      'score_help',
      'game_score_help',
      'feedback_support',
      'error',
    ),
    method: oneOf('close', 'escape', 'outside'),
  },
  // Problems players hit (errorReporting.ts). Their props become Clarity tags,
  // so each gets its own key rather than a shared `source` or `context`.
  app_error: { error_source: oneOf('error', 'rejection', 'render', 'route') },
  chunk_load_failed: {
    chunk_outcome: oneOf(
      'reloaded',
      'same_version',
      'check_failed',
      'just_reloaded',
    ),
  },
  sw_register_failed: {},
  live_share_failed: { share_stage: oneOf('start', 'write', 'watch') },
  storage_failed: { storage_operation: oneOf('read', 'write') },
  link_copy_failed: {},
} as const;
export type AnalyticsEvent = keyof typeof EVENT_SCHEMA;

const VERCEL_EVENTS = [
  'real_game_qualified',
  'game_started',
  'game_continued',
  'game_won',
  'viewer_session_joined',
  'pwa_installed',
  'offline_play_synced',
  'screen_session_started',
  'nil_scoring_rule_changed',
  'ui_mode_changed',
  'install_requested',
  'app_error',
  'chunk_load_failed',
  'sw_register_failed',
  'live_share_failed',
  'storage_failed',
] as const satisfies readonly AnalyticsEvent[];

/**
 * Vercel bills every custom event (50k a month on Hobby) and Clarity doesn't,
 * so only the few events per game that Vercel's Events tab is used to count
 * (funnel, install, device and problems) go to Vercel. Every event goes to
 * Clarity, including the per-tap ones (about 20 a round). A Record, not a
 * list: a new event must choose a side to compile.
 */
export const SENDS_TO_VERCEL: Record<AnalyticsEvent, boolean> =
  Object.fromEntries(
    (Object.keys(EVENT_SCHEMA) as AnalyticsEvent[]).map((name) => [
      name,
      (VERCEL_EVENTS as readonly string[]).includes(name),
    ]),
  ) as Record<AnalyticsEvent, boolean>;
export function isAnalyticsEvent(name: string): name is AnalyticsEvent {
  return Object.hasOwn(EVENT_SCHEMA, name);
}

/** Use at CTA sites; never derive annotations from user content. */
export function trackProps<E extends AnalyticsEvent>(
  name: E,
  data?: Partial<
    Record<keyof (typeof EVENT_SCHEMA)[E], string | number | boolean>
  >,
) {
  return {
    'data-track': name,
    ...(data ? { 'data-track-props': JSON.stringify(data) } : {}),
  };
}

export type AnalyticsEventData = Record<string, string | number | boolean>;

/**
 * Thin wrapper around Vercel's `track` so components never depend on the
 * analytics vendor directly. Analytics must never break the app: failures are
 * swallowed and there is a no-op path when `window` is unavailable (SSR/tests).
 */
export function trackEvent(
  name: AnalyticsEvent,
  data?: AnalyticsEventData,
): void {
  if (typeof window === 'undefined') return;
  if (!isAnalyticsEvent(name)) return;
  const schema: Record<string, Validator> = EVENT_SCHEMA[name];
  const safeData =
    data === undefined
      ? undefined
      : Object.fromEntries(
          Object.entries(data).filter(
            ([key, value]) => Object.hasOwn(schema, key) && schema[key](value),
          ),
        );
  // Build version is not a caller prop: unknown keys, including a supplied
  // app_version, are already dropped. Events without it are an older build.
  if (SENDS_TO_VERCEL[name]) {
    const vercelData = { ...safeData, app_version: appVersion() };
    try {
      track(name, vercelData);
    } catch {
      /* Best effort; vendors are independent. */
    }
  }
  try {
    trackClarityEvent(name, safeData);
  } catch {
    /* Best effort. */
  }
}
